import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiBell,
  FiBellOff,
  FiMail,
  FiMessageSquare,
  FiUsers,
  FiCheck,
  FiLoader,
  FiTrash2,
  FiCheckCircle,
  FiInbox,
  FiRefreshCw,
} from "react-icons/fi";
import { createAuthAxios } from "../../../utils/apiUtils";
import { toast } from "react-toastify";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
    },
  },
};

const NotificationSettings = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isMarkingAll, setIsMarkingAll] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
  }, []);

  const fetchNotifications = async (pageNum = 1) => {
    setIsLoading(true);
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get(
        `/notifications?page=${pageNum}&limit=20`
      );

      if (response.data.success) {
        if (pageNum === 1) {
          setNotifications(response.data.notifications || []);
        } else {
          setNotifications((prev) => [
            ...prev,
            ...(response.data.notifications || []),
          ]);
        }
        setHasMore(
          response.data.pagination?.page < response.data.pagination?.totalPages
        );
        setPage(pageNum);
      }
    } catch (error) { /* ignored */ } finally {
      setIsLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get("/notifications/unread-count");
      if (response.data.success) {
        setUnreadCount(response.data.unreadCount);
      }
    } catch (error) { /* ignored */ }
  };

  const markAsRead = async (notificationId) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.put(
        `/notifications/${notificationId}/read`
      );

      if (response.data.success) {
        setNotifications((prev) =>
          prev.map((n) => (n._id === notificationId ? { ...n, read: true } : n))
        );
        setUnreadCount(response.data.unreadCount);
      }
    } catch (error) {
      toast.error("Failed to mark notification as read");
    }
  };

  const markAllAsRead = async () => {
    setIsMarkingAll(true);
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.put("/notifications/read-all");

      if (response.data.success) {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        setUnreadCount(0);
        toast.success("All notifications marked as read");
      }
    } catch (error) {
      toast.error("Failed to mark all as read");
    } finally {
      setIsMarkingAll(false);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.delete(
        `/notifications/${notificationId}`
      );

      if (response.data.success) {
        setNotifications((prev) =>
          prev.filter((n) => n._id !== notificationId)
        );
        setUnreadCount(response.data.unreadCount);
        toast.success("Notification deleted");
      }
    } catch (error) {
      toast.error("Failed to delete notification");
    }
  };

  const clearAllNotifications = async () => {
    setIsClearing(true);
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.delete("/notifications/clear-all");

      if (response.data.success) {
        setNotifications([]);
        setUnreadCount(0);
        toast.success("All notifications cleared");
      }
    } catch (error) {
      toast.error("Failed to clear notifications");
    } finally {
      setIsClearing(false);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      message: <FiMessageSquare />,
      follow: <FiUsers />,
      like: <FiCheck />,
      comment: <FiMessageSquare />,
      mention: <FiMail />,
    };
    return icons[type] || <FiBell />;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="stg-content-wrapper"
    >
      {/* Section Header */}
      <div className="stg-section-header">
        <h1 className="stg-section-title">
          <span className="stg-section-title-icon stg-amber">
            <FiBell />
          </span>
          Notifications
        </h1>
        <p className="stg-section-description">
          Manage your notifications and notification preferences
        </p>
      </div>

      {/* Quick Stats */}
      <motion.div variants={itemVariants} className="stg-card">
        <div className="stg-card-header">
          <h3 className="stg-card-title">
            <FiInbox /> Notification Center
          </h3>
          <div className="stg-card-actions">
            {unreadCount > 0 && (
              <span className="stg-status stg-status-info">
                {unreadCount} unread
              </span>
            )}
          </div>
        </div>
        <div className="stg-card-body" style={{ padding: "12px 24px" }}>
          <div className="stg-flex stg-gap-md" style={{ flexWrap: "wrap" }}>
            <button
              className="stg-btn stg-btn-secondary stg-btn-sm"
              onClick={() => fetchNotifications(1)}
              disabled={isLoading}
            >
              <FiRefreshCw className={isLoading ? "stg-spin" : ""} /> Refresh
            </button>
            <button
              className="stg-btn stg-btn-secondary stg-btn-sm"
              onClick={markAllAsRead}
              disabled={isMarkingAll || unreadCount === 0}
            >
              {isMarkingAll ? (
                <FiLoader className="stg-spin" />
              ) : (
                <FiCheckCircle />
              )}{" "}
              Mark All Read
            </button>
            <button
              className="stg-btn stg-btn-danger stg-btn-sm"
              onClick={clearAllNotifications}
              disabled={isClearing || notifications.length === 0}
            >
              {isClearing ? <FiLoader className="stg-spin" /> : <FiTrash2 />}{" "}
              Clear All
            </button>
          </div>
        </div>
      </motion.div>

      {/* Notifications List */}
      <motion.div variants={itemVariants} className="stg-card">
        <div className="stg-card-header">
          <h3 className="stg-card-title">
            <FiBell /> Recent Notifications
          </h3>
        </div>
        <div className="stg-card-body" style={{ padding: 0 }}>
          {isLoading && notifications.length === 0 ? (
            <div className="stg-loading">
              <div className="stg-spinner"></div>
            </div>
          ) : notifications.length > 0 ? (
            <div className="stg-activity-list" style={{ padding: "0 24px" }}>
              <AnimatePresence>
                {notifications.map((notification) => (
                  <motion.div
                    key={notification._id}
                    className="stg-activity-item"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    style={{
                      opacity: notification.read ? 0.7 : 1,
                      background: notification.read
                        ? "transparent"
                        : "rgba(99, 102, 241, 0.03)",
                      margin: "0 -24px",
                      padding: "16px 24px",
                    }}
                  >
                    <div
                      className={`stg-activity-icon ${
                        notification.read ? "stg-info" : "stg-success"
                      }`}
                    >
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="stg-activity-content" style={{ flex: 1 }}>
                      <p
                        className="stg-activity-title"
                        style={{ fontWeight: notification.read ? 400 : 600 }}
                      >
                        {notification.message || notification.title}
                      </p>
                      <p className="stg-activity-meta">
                        {formatDate(notification.createdAt)}
                      </p>
                    </div>
                    <div className="stg-flex stg-gap-sm">
                      {!notification.read && (
                        <button
                          className="stg-btn stg-btn-ghost stg-btn-icon stg-btn-sm"
                          onClick={() => markAsRead(notification._id)}
                          title="Mark as read"
                        >
                          <FiCheck size={14} />
                        </button>
                      )}
                      <button
                        className="stg-btn stg-btn-ghost stg-btn-icon stg-btn-sm"
                        onClick={() => deleteNotification(notification._id)}
                        title="Delete"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <div className="stg-empty">
              <div className="stg-empty-icon">
                <FiBellOff />
              </div>
              <h4 className="stg-empty-title">No notifications</h4>
              <p className="stg-empty-text">
                You're all caught up! New notifications will appear here.
              </p>
            </div>
          )}

          {hasMore && notifications.length > 0 && (
            <div style={{ padding: "16px 24px", textAlign: "center" }}>
              <button
                className="stg-btn stg-btn-secondary"
                onClick={() => fetchNotifications(page + 1)}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <FiLoader className="stg-spin" /> Loading...
                  </>
                ) : (
                  "Load More"
                )}
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default NotificationSettings;
